import { LegalLayout, Sez } from "@/components/LegalLayout";
import { useSeo } from "@/hooks/useSeo";

const Mail = ({ a }) => (
  <a href={`mailto:${a}`} className="di-underline text-[#5E2A52]">
    {a}
  </a>
);

export default function Privacy() {
  useSeo({
    title: "Privacy policy | DonnaImpresa",
    description:
      "Informativa privacy di DonnaImpresa ai sensi degli artt. 13-14 GDPR: titolare del trattamento BC NEXT, dati raccolti dai moduli del sito, finalità, conservazione e diritti dell'interessato.",
    path: "/privacy",
  });

  return (
    <LegalLayout title="Privacy policy" updated="Giugno 2026" testId="privacy-page">
      <p>
        La presente informativa è resa ai sensi degli artt. 13 e 14 del
        Regolamento (UE) 2016/679 (di seguito «GDPR») e descrive le modalità di
        trattamento dei dati personali degli utenti che consultano il sito
        donnaimpresa.net e che inviano i moduli di contatto presenti al suo
        interno.
      </p>

      <Sez titolo="1. Titolare del trattamento">
        <p>
          Il titolare del trattamento è <strong>BC NEXT S.r.l. — IT Consulting</strong>,
          che gestisce e mantiene la piattaforma DonnaImpresa:
        </p>
        <ul className="mt-3 space-y-1.5">
          <li>Sede: Via Francesco Brighindi 37, 03100 Frosinone (FR), Italia</li>
          <li>Partita IVA: 03291880601</li>
          <li>Telefono: +39 392 079 8266</li>
          <li>
            Email: <Mail a="info@bcnext.it" />
          </li>
        </ul>
        <p className="mt-3">
          Per le richieste inviate tramite i moduli del sito puoi scrivere anche
          all'indirizzo operativo del progetto DonnaImpresa:{" "}
          <Mail a="impresahub@gmail.com" />. Il titolare non ha nominato un
          Responsabile della protezione dei dati (DPO), non essendo ricorrente
          alcuna delle ipotesi di cui all'art. 37 GDPR.
        </p>
      </Sez>

      <Sez titolo="2. Dati personali trattati">
        <p>
          <strong>a) Dati forniti volontariamente.</strong> Attraverso i moduli
          «Racconta la tua storia» e «Diventa sponsor» raccogliamo: nome e
          cognome, denominazione dell'attività o azienda, indirizzo email,
          numero di telefono (facoltativo), settore o obiettivo, il testo del
          messaggio o della storia e la conferma del consenso privacy.
        </p>
        <p>
          <strong>b) Dati di navigazione.</strong> Per finalità di sicurezza e
          prevenzione degli abusi il sistema registra l'indirizzo IP da cui
          viene inviato il modulo e la data e ora dell'invio.
        </p>
        <p>
          <strong>c) Cookie.</strong> Il sito utilizza esclusivamente cookie e
          strumenti tecnici necessari al funzionamento, come descritto nella{" "}
          <a href="/cookie-policy" className="di-underline text-[#5E2A52]">
            cookie policy
          </a>
          .
        </p>
        <p>
          Il sito non è rivolto a minori di 16 anni e non raccoglie
          consapevolmente dati di minori. Non vengono trattate categorie
          particolari di dati (art. 9 GDPR): ti invitiamo a non inserire nei
          messaggi informazioni relative a salute, opinioni politiche,
          convinzioni religiose o altri dati sensibili.
        </p>
      </Sez>

      <Sez titolo="3. Finalità e base giuridica">
        <ul className="space-y-2">
          <li>
            <strong>Gestire e rispondere alla tua richiesta</strong> (adesione
            alla rete, proposta della tua storia, richiesta di partnership),
            compreso l'invio dell'email automatica di conferma. Base giuridica:
            consenso dell'interessato, art. 6.1.a GDPR, prestato con la
            spunta obbligatoria nel modulo.
          </li>
          <li>
            <strong>Pubblicare la tua storia</strong> sul sito e sui canali
            social della rete. Base giuridica: consenso specifico, richiesto
            separatamente al momento del contatto; nessuna storia viene
            pubblicata senza la tua approvazione espressa sui contenuti
            definitivi.
          </li>
          <li>
            <strong>Sicurezza del sito</strong> e prevenzione di invii
            automatici o abusivi. Base giuridica: legittimo interesse del
            titolare, art. 6.1.f GDPR.
          </li>
          <li>
            <strong>Adempimenti di legge</strong> eventualmente applicabili.
            Base giuridica: obbligo legale, art. 6.1.c GDPR.
          </li>
        </ul>
        <p className="mt-3">
          Il conferimento dei dati è facoltativo, ma il rifiuto di fornire i
          dati contrassegnati come obbligatori rende impossibile dare seguito
          alla richiesta. I dati non sono utilizzati per finalità di marketing
          né per processi decisionali automatizzati o profilazione.
        </p>
      </Sez>

      <Sez titolo="4. Destinatari e responsabili del trattamento">
        <p>
          I dati sono trattati dal personale autorizzato del titolare e possono
          essere comunicati a fornitori che agiscono come responsabili del
          trattamento ai sensi dell'art. 28 GDPR:
        </p>
        <ul className="mt-3 space-y-1.5">
          <li>
            <strong>Brevo</strong> (Sendinblue SAS, Francia) — invio delle email
            di notifica e di conferma;
          </li>
          <li>
            <strong>Emergent</strong> — hosting dell'applicazione e del database
            in cui sono conservate le richieste;
          </li>
          <li>
            <strong>BC NEXT</strong> — manutenzione tecnica e assistenza della
            piattaforma.
          </li>
        </ul>
        <p className="mt-3">
          I dati non sono diffusi né ceduti a terzi per finalità commerciali.
          Qualora un fornitore comporti un trasferimento di dati verso paesi
          terzi, il trasferimento avviene sulla base di decisioni di adeguatezza
          o di clausole contrattuali standard approvate dalla Commissione
          europea (artt. 45-46 GDPR). L'elenco aggiornato dei responsabili è
          disponibile scrivendo a <Mail a="info@bcnext.it" />.
        </p>
      </Sez>

      <Sez titolo="5. Periodo di conservazione">
        <p>
          Le richieste inviate tramite i moduli sono conservate per il tempo
          necessario a gestirle e, in caso di adesione alla rete, per la durata
          del rapporto, e comunque non oltre <strong>24 mesi</strong> dall'ultimo
          contatto, salvo revoca anticipata del consenso o diverso obbligo di
          legge. I dati tecnici raccolti per finalità di sicurezza sono
          conservati per un massimo di 12 mesi. Alla scadenza i dati sono
          cancellati o resi anonimi.
        </p>
      </Sez>

      <Sez titolo="6. Diritti dell'interessato">
        <p>
          In qualsiasi momento puoi esercitare i diritti previsti dagli artt.
          15-22 GDPR: accesso ai tuoi dati, rettifica, cancellazione
          («diritto all'oblio»), limitazione del trattamento, portabilità,
          opposizione al trattamento e revoca del consenso — la revoca non
          pregiudica la liceità dei trattamenti già effettuati.
        </p>
        <p>
          Per esercitarli scrivi a <Mail a="info@bcnext.it" /> oppure a{" "}
          <Mail a="impresahub@gmail.com" />: risponderemo senza ingiustificato
          ritardo e comunque entro un mese dalla richiesta. Hai inoltre il
          diritto di proporre reclamo all'Autorità Garante per la protezione dei
          dati personali (Piazza Venezia 11, 00187 Roma — www.garanteprivacy.it)
          o di ricorrere all'autorità giudiziaria.
        </p>
      </Sez>

      <Sez titolo="7. Sicurezza dei dati">
        <p>
          Il sito è servito esclusivamente tramite connessione cifrata HTTPS.
          L'accesso alle richieste ricevute è protetto da autenticazione con
          credenziali personali e sessioni cifrate; sono applicate misure
          tecniche e organizzative adeguate ai sensi dell'art. 32 GDPR, tra cui
          limitazione degli accessi, registrazione delle attività e limiti
          contro gli invii automatizzati.
        </p>
      </Sez>

      <Sez titolo="8. Modifiche">
        <p>
          La presente informativa può essere aggiornata; le modifiche
          significative saranno segnalate su questa pagina insieme alla data di
          ultimo aggiornamento indicata in alto.
        </p>
      </Sez>
    </LegalLayout>
  );
}
